import { aboutInfo } from '@/mocks/jiese';
import SEOHead from '@/components/feature/SEOHead';
import { useNavigate } from 'react-router-dom';
import { getFormUrl } from '@/hooks/useFormUrl';
import { useState, useEffect, useRef } from 'react';

export default function AboutPage() {
  const navigate = useNavigate();
  const [formUrl, setFormUrl] = useState<string>('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    getFormUrl('戒色完全指南 - 联系我们表单').then((url) => setFormUrl(url)).catch(() => setFormUrl(''));
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formUrl) return;

    setSubmitting(true);
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('website_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      setSubmitting(false);
      form.reset();
      return;
    }
    formData.delete('website_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === 'string') {
          params.append(key, value);
        }
      });

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed && typeof parsed === 'object') ? ((parsed as Record<string, unknown>).meta as Record<string, unknown>)?.message || (parsed as Record<string, unknown>).message || responseText : responseText;
        setFormError(typeof msg === 'string' ? msg : '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 戒色完全指南"
        description="了解戒色完全指南的品牌故事、核心使命与价值观。我们致力于用科学方法帮助有戒色需求的人群实现身心健康。提供品牌介绍、声望展示与联系方式。"
        keywords="戒色,关于我们,戒色指南,戒色团队,戒色联系方式"
        canonicalUrl="https://purifypath.github.io/about"
        breadcrumbList={[
          { name: '首页', url: 'https://purifypath.github.io/' },
          { name: '关于我们', url: 'https://purifypath.github.io/about' },
        ]}
      />

      <main className="pt-16">
        {/* Hero */}
        <section className="bg-background-100 py-14 md:py-20">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-5 flex-wrap" aria-label="Breadcrumb">
              <button onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); navigate('/'); }} className="hover:text-primary-600 transition-colors cursor-pointer">首页</button>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">关于我们</span>
            </nav>

            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              关于戒色完全指南
            </h1>
            <p className="text-base md:text-lg text-foreground-600 max-w-[700px] leading-relaxed">
              {aboutInfo.description}
            </p>
          </div>
        </section>

        {/* Mission */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="max-w-[700px] mx-auto text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-flag-line text-2xl"></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                我们的使命
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed">
                {aboutInfo.mission}
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                核心价值观
              </h2>
              <p className="text-sm text-foreground-600 mt-2">我们恪守的原则与信念</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {aboutInfo.values.map((v, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg p-6 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`text-xl ${['ri-flask-line', 'ri-lock-line', 'ri-tools-line', 'ri-group-line'][idx]}`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation / Stats */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                平台声望
              </h2>
              <p className="text-sm text-foreground-600 mt-2">用数据说话，见证我们的成长</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {[
                { value: '10,000+', label: '累计访问用户', icon: 'ri-user-star-line' },
                { value: '200+', label: '原创科学文章', icon: 'ri-article-line' },
                { value: '89%', label: '读者满意度', icon: 'ri-emotion-happy-line' },
                { value: '50,000+', label: '累计打卡次数', icon: 'ri-calendar-check-line' },
              ].map((stat) => (
                <div key={stat.label} className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-950 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 mt-2">有任何问题或建议，欢迎随时与我们联系</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-[1000px] mx-auto">
              {/* Contact Info */}
              <div className="flex flex-col gap-5">
                <div className="flex items-start gap-4 p-5 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮件</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">微信</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-group-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">QQ 群</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.qqGroup}</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6">
                <h3 className="text-lg font-semibold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  发送消息
                </h3>

                {formUrl ? (
                  <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col gap-4" data-readdy-form="">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">留言</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      ></textarea>
                    </div>
                    {/* Honeypot */}
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? '发送中...' : '发送消息'}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-primary-600 text-center">消息已发送，我们会尽快回复您！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-accent-600 text-center">{formError}</p>
                    )}
                  </form>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-sm text-foreground-500">表单加载中...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}